<script setup>
import { defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { registerSnapshotCaptureHost, unregisterSnapshotCaptureHost } from '@/common/snapshotCapture';

// Renders snapshots captured when a completed form without one is opened (snapshotCapture.js).
// The print renderer loads on the first capture and is mounted only while one runs.
const loadFormRenderPrint = () => import('@/components/questionnaires/form/BnFormRenderPrint.vue');
const BnFormRenderPrint = defineAsyncComponent(loadFormRenderPrint);

const capturing = ref(false);
const progressMessage = ref(null);
const formRenderPrintRef = ref(null);

let resolvePrinter = null;
watch(formRenderPrintRef, (printer) => {
    if (printer && resolvePrinter) {
        resolvePrinter(printer);
        resolvePrinter = null;
    }
});

const render = async (printData) => {
    capturing.value = true;
    try {
        await loadFormRenderPrint();
        const printer = formRenderPrintRef.value || (await new Promise((resolve) => (resolvePrinter = resolve)));
        return await printer.printFormView(printData, { captureOnly: true });
    } finally {
        resolvePrinter = null;
        capturing.value = false;
    }
};

const setProgress = (message) => {
    progressMessage.value = message || null;
};

const host = { render, setProgress };

onMounted(() => registerSnapshotCaptureHost(host));
onBeforeUnmount(() => unregisterSnapshotCaptureHost(host));
</script>

<template>
    <v-overlay :model-value="!!progressMessage" class="align-center justify-center" persistent data-cy="snapshotCaptureOverlay">
        <v-card class="d-flex flex-column align-center pa-6" rounded="lg" elevation="8">
            <v-progress-circular indeterminate color="primary" size="48" />
            <div class="mt-3">{{ progressMessage }}</div>
        </v-card>
    </v-overlay>
    <BnFormRenderPrint v-if="capturing" ref="formRenderPrintRef" />
</template>
